'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '@kit/ui/utils';

import styles from './website-ambient-background.module.css';

export interface WebsiteAmbientBackgroundProps {
  variant?: 'hero' | 'soft' | 'workflow';
  className?: string;
}

/**
 * Decorative colour ribbons. Mount inside a relative, isolated, clipped parent.
 * CSS owns every animation frame; React only changes playback at visibility edges.
 */
export function WebsiteAmbientBackground({
  variant = 'hero',
  className,
}: WebsiteAmbientBackgroundProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let inView = false;

    const syncPlayback = () => {
      setIsPlaying(inView && !document.hidden && !reducedMotion.matches);
    };

    // Without an observer the decoration stays static, rather than running unseen.
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? undefined
        : new IntersectionObserver(
            ([entry]) => {
              inView = entry?.isIntersecting ?? false;
              syncPlayback();
            },
            { threshold: 0 },
          );

    observer?.observe(root);
    document.addEventListener('visibilitychange', syncPlayback);
    reducedMotion.addEventListener('change', syncPlayback);

    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', syncPlayback);
      reducedMotion.removeEventListener('change', syncPlayback);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      data-website-aura
      aria-hidden="true"
      className={cn(
        styles.ambient,
        styles[variant],
        isPlaying ? styles.playing : styles.paused,
        className,
      )}
    >
      <span className={styles.wash} />
      <span className={cn(styles.ribbon, styles.ribbonPrimary)} />
      <span className={cn(styles.ribbon, styles.ribbonBlue)} />
      <span className={cn(styles.ribbon, styles.ribbonPeach)} />
    </div>
  );
}
